import './Input.less';

import React, { forwardRef, ReactNode } from 'react';
import { useReframe } from 'nike-re-framejs';
import clsx from 'clsx';
import { makeField, NfInput, TOUCH_ON_BLUR_ONLY_IF_CHANGED } from 'nike-form-js';
import { getDateCoercer } from './Date';
import FieldErrors from './FieldErrors';
import { List } from 'immutable';
import i18n from '../../common/i18n';

interface InputProps {
    form?: any;
    path?: any;
    label?: ReactNode;
    size?: string;
    hint?: string;
    type?: string;
    disabled?: boolean;
    autocomplete?: string;
    autocapitalize?: string;
    className?: string;
    touchOnChange?: boolean;
    touchOnBlur?: any;
    onInput?: any;
    children?: ReactNode;
    dataAtid?: string;
    value?: any;
    hasError?: any;
    errorText?: any;
    name?: any;
    placeholder?: any;
    loginInput?: any;
    loginErrors?: any;
    inputClassName?: string;
    optional?: boolean;
    style?: 'normal' | 'black';
    money?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            form,
            path,
            label,
            size,
            hint,
            type,
            name,
            placeholder,
            disabled = false,
            autocomplete = 'off',
            autocapitalize,
            className,
            touchOnChange = true,
            touchOnBlur = TOUCH_ON_BLUR_ONLY_IF_CHANGED,
            onInput,
            loginInput,
            loginErrors,
            children,
            dataAtid,
            value,
            hasError,
            errorText,
            inputClassName,
            optional,
            style = 'normal',
            money,
            ...rest
        },
        ref
    ) => {
        const field = form && makeField(form, path, type === 'date' ? getDateCoercer() : undefined);
        const { deref } = useReframe();
        const errors = field && deref(field.getErrors());
        const isTouched = field && deref(field.isTouched());
        const isError = field ? isTouched && errors.size > 0 : hasError;
        const id = '' + path;
        type = money ? 'number' : type;

        return (
            <label
                data-path={path && path[0]}
                className={clsx([
                    'nui-input p-rel mb-20 flex flex-col',
                    disabled ? 'nui-input-disabled' : null,
                    className
                ])}
            >
                {label ? (
                    <div className="nui-input-label fs-14">
                        {optional && <span className="pull-right c-black-50-2 fs-12">{i18n('nui.form.optional')}</span>}
                        <span className={clsx('text-extra-bold fs-14', style === 'black' && 'c-white')}>{label}</span>
                    </div>
                ) : null}
                {hint ? <div className="nui-input-hint fs-12">{hint}</div> : null}
                {form ? (
                    <NfInput
                        field={field}
                        attrs={Object.assign({}, rest, {
                            id,
                            type,
                            name: name,
                            autoComplete: autocomplete,
                            autoCapitalize: autocapitalize,
                            placeholder: placeholder,
                            disabled,
                            touchOnChange: touchOnChange,
                            touchOnBlur: touchOnBlur,
                            onInput: onInput,
                            className: clsx(
                                'nui-native-input',
                                size,
                                style !== 'normal' && style,
                                { 'nui-input-error': isError || loginErrors },
                                inputClassName
                            ),
                            ref: ref,
                            'data-atid': dataAtid
                        })}
                    />
                ) : (
                    <input
                        ref={ref}
                        type={type}
                        autoComplete={autocomplete}
                        autoCapitalize={autocapitalize}
                        disabled={disabled}
                        value={value}
                        className={clsx(
                            'nui-native-input',
                            size,
                            style !== 'normal' && style,
                            { 'nui-input-error': isError },
                            { 'data-atid': dataAtid }
                        )}
                        {...rest}
                    />
                )}
                {money && <span className="nui-native-input-currency c-white p-abs">€</span>}
                {children}
                {isError && !loginInput ? <FieldErrors errors={errors || List([errorText])} className /> : null}
            </label>
        );
    }
);

export default Input;
